import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { CrossIcon, SvgComponent } from '../../assets';
import { breakpoint, theme } from '../../theme';
import { PrimaryButton, SecondaryButton } from '../button';
import { InfoPanelHeader } from './InfoPanelHeader';
import { InfoPanelContainer } from './InfoPanelContainer';
import { InfoPanelSize, InfoPanelType } from './types';

export type InfoPanelProps = PropsWithChildren<{
  actionLabel?: string;
  actionIcon?: SvgComponent;
  onActionClick?: () => void;
  onClose?: () => void;
  link?: string;
  linkLabel?: string;
  onLinkClick?: () => void;
  title: string;
  type?: InfoPanelType;
  titleIcon?: SvgComponent;
  size?: InfoPanelSize;
  isPrimaryButton?: boolean;
  withMarginLeft?: boolean;
  buttonSize?: 'small' | 'medium' | 'large';
}>;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${breakpoint('sm')`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `};
`;

const Cross = styled(CrossIcon)`
  cursor: pointer;
  fill: ${theme.colors.primary.graysGray};
  margin: ${theme.spacings[1].rem} 0 0 ${theme.spacings[2].rem};
  width: 16px;

  ${breakpoint('sm')`
    margin: ${theme.spacings[1].rem} 0 0 ${theme.spacings[4].rem};
  `};
`;

const Children = styled.div<{ withMarginLeft?: boolean }>`
  margin-top: ${theme.spacings[1].rem};

  ${(props) => props.withMarginLeft && `margin-left: 1.375rem;`}

  ${breakpoint('sm')`
    margin-top: ${theme.spacings[1].rem};
    ${(props: { withMarginLeft?: boolean }) =>
      props.withMarginLeft && `margin-left: ${theme.spacings[6].rem};`}
  `};
`;

const ButtonWrapper = styled.div`
  margin: ${theme.spacings[2].rem} 0 0 0;

  ${breakpoint('sm')`
    margin: 0 0 0 ${theme.spacings[4].rem};
  `};
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  padding: ${theme.spacings[3].rem} ${theme.spacings[4.75].rem};
  margin-bottom: ${theme.spacings[4].rem};
  ${breakpoint('sm')`
    margin-bottom: 0;
  `};
`;

const Button = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: normal;
  ${breakpoint('sm')`
    ${theme.typography.body.md};
    font-weight: 700;
  `};
`;

const ActionIconWrapper = styled.span`
  display: flex;
  margin-right: ${theme.spacings[3].rem};
`;

export const InfoPanel: React.FC<InfoPanelProps> = ({
  actionLabel,
  actionIcon: ActionIcon,
  children,
  link,
  linkLabel,
  onLinkClick,
  onActionClick,
  onClose,
  title,
  type = 'info',
  titleIcon,
  size = 'lg',
  isPrimaryButton = true,
  withMarginLeft = false,
  buttonSize = 'large',
}) => {
  return (
    <InfoPanelContainer type={type} size={size}>
      <Content>
        <div>
          <InfoPanelHeader
            titleIcon={titleIcon}
            type={type}
            title={title}
            link={link}
            linkLabel={linkLabel}
            onLinkClick={onLinkClick}
          />

          {children && (
            <Children withMarginLeft={withMarginLeft}>{children}</Children>
          )}
        </div>
        {actionLabel && onActionClick && (
          <ButtonWrapper>
            <Button
              as={isPrimaryButton ? PrimaryButton : StyledSecondaryButton}
              fullWidth={true}
              onClick={onActionClick}
              size={buttonSize}
            >
              {ActionIcon && (
                <ActionIconWrapper>
                  <ActionIcon />
                </ActionIconWrapper>
              )}
              {actionLabel}
            </Button>
          </ButtonWrapper>
        )}
      </Content>
      {onClose && <Cross onClick={onClose} />}
    </InfoPanelContainer>
  );
};
